import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, TextField, Button, Typography, Link, Stack, Alert } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { isMfaChallenge } from "../api/AuthApi";

// Zachte kaart (ruimte + schaduw i.p.v. rand) — zelfde stijl als
// theme/marketingStyles.softCard.
const card = { p: { xs: 3, sm: 4 }, borderRadius: "var(--radius-xl)", bgcolor: "var(--surface)", boxShadow: "var(--shadow-md)" } as const;

/** Register/login form. Handles the MFA second-factor step when login requires it. */
export default function AuthPanel({ onSuccess, initialMode = "register", role = "client" }: { onSuccess?: () => void; initialMode?: "register" | "login"; role?: string }) {
  const { t } = useTranslation();
  const { login, register, verifyMfa } = useAuth();
  const [mode, setMode] = useState<"register" | "login">(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");

  const submit = async () => {
    setError(""); setBusy(true);
    try {
      if (mode === "register") { await register({ email, password, name: name || undefined, role }); onSuccess?.(); }
      else {
        const res = await login(email, password);
        if (isMfaChallenge(res)) { setMfaToken(res.mfaToken); setBusy(false); return; }
        onSuccess?.();
      }
    } catch (e: any) { setError(e?.response?.data?.error || t("auth.error")); setBusy(false); }
  };

  const submitMfa = async () => {
    setError(""); setBusy(true);
    try { await verifyMfa(mfaToken!, mfaCode.trim()); onSuccess?.(); }
    catch { setError(t("mfa.badCode")); setBusy(false); }
  };

  // --- MFA second factor ---
  if (mfaToken) {
    return (
      <Box sx={card}>
        <Typography variant="h2" sx={{ fontSize: "1.4rem", mb: 1 }}>{t("mfa.verifyTitle")}</Typography>
        <Typography sx={{ color: "text.secondary", mb: 2.5 }}>{t("mfa.verifyBody")}</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2} component="form" onSubmit={(e) => { e.preventDefault(); submitMfa(); }}>
          <TextField label={t("mfa.code")} value={mfaCode} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMfaCode(e.target.value)}
            autoFocus fullWidth inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }} />
          <Button type="submit" variant="contained" size="large" disabled={busy || mfaCode.trim().length < 6}>
            {busy ? t("funnel.submitting") : t("mfa.verifySubmit")}
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={card}>
      <Typography variant="h2" sx={{ fontSize: "1.4rem", mb: 2.5 }}>
        {mode === "register" ? t("auth.titleRegister") : t("auth.titleLogin")}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Stack spacing={2} component="form" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        {mode === "register" && (
          <TextField label={t("auth.name")} value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} fullWidth />
        )}
        <TextField label={t("auth.email")} type="email" value={email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} fullWidth required />
        <TextField label={t("auth.password")} type="password" value={password} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
          helperText={mode === "register" ? t("auth.passwordHint") : undefined} fullWidth required />
        {mode === "login" && (
          <Link component={RouterLink} to="/wachtwoord-vergeten" sx={{ fontSize: ".9rem", alignSelf: "flex-start" }}>
            {t("auth.forgotPassword")}
          </Link>
        )}
        <Button type="submit" variant="contained" size="large" disabled={busy || !email || password.length < 8}>
          {busy ? t("funnel.submitting") : mode === "register" ? t("auth.submitRegister") : t("auth.submitLogin")}
        </Button>
      </Stack>
      <Typography sx={{ mt: 2, fontSize: ".9rem", color: "text.secondary" }}>
        {mode === "register" ? t("auth.haveAccount") : t("auth.noAccount")}{" "}
        <Link component="button" type="button" onClick={() => { setMode(mode === "register" ? "login" : "register"); setError(""); }}>
          {mode === "register" ? t("auth.toggleToLogin") : t("auth.toggleToRegister")}
        </Link>
      </Typography>
    </Box>
  );
}
